import { type ReactNode, useMemo } from 'react';
import { Button, type ButtonProps } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { UnescapedTrans, defaultNS, useTranslation } from '@/lib/i18n';
type DialogTitleType = {
  resource: string;
  action: string;
};
export type ConfirmActionDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: DialogTitleType | string;
  description: string | ReactNode;
  okLabel?: string;
  okVariant?: ButtonProps['variant'];
  cancelLabel?: string;
  cancelVariant?: ButtonProps['variant'];
  /** 実行中は両ボタンを無効化し、確定の二重送信を防ぐ。 */
  isLoading?: boolean;
};

/**
 * アクションの実行前に確認を行うための汎用的なダイアログコンポーネント
 *
 * 主な用途:
 * - リソースの削除
 * - アカウントの停止
 * - 重要な変更の確認
 * など、取り消しできない操作やユーザーに確認が必要な操作の前に表示する
 *
 * 特徴:
 * - タイトルはリソース名とアクション名から生成
 * - キャンセルと実行の2つのアクションボタンを提供
 */

export function ConfirmActionDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  okVariant = 'default',
  okLabel,
  cancelVariant = 'outline',
  cancelLabel,
  isLoading = false
}: ConfirmActionDialogProps) {
  const tc = useCommonTranslation();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'component.ConfirmDialog'
  });
  const formattedTitle = useMemo(() => {
    if (typeof title === 'string') {
      return title;
    } else {
      return t('title', {
        resource: title.resource,
        action: title.action
      });
    }
  }, [title, t]);
  const formattedOkLabel = useMemo(() => {
    if (typeof okLabel === 'string') {
      return okLabel;
    } else if (typeof title === 'object') {
      return title.action;
    } else {
      return 'OK';
    }
  }, [okLabel, title]);
  return <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <UnescapedTrans>{formattedTitle}</UnescapedTrans>
          </DialogTitle>
        </DialogHeader>
        {typeof description === 'string' ? <div className="py-4 whitespace-pre-line">{description}</div> : description}
        <DialogFooter>
          <Button variant={cancelVariant} onClick={onClose} disabled={isLoading}>
            {cancelLabel ?? tc('action.cancel')}
          </Button>
          <Button variant={okVariant} onClick={onConfirm} disabled={isLoading}>
            {formattedOkLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>;
}